/**
 * How one action draws its calendar (D29): the grid and the door number; and how its doors
 * look (D31): the door, today's mark, the days that have passed, the night, the empty places,
 * the decoration, the tilt of the numbers and the place of a logo.
 *
 * The shapes are the API's (`CalendarGrid`, `DoorNumberStyle` in the backend's
 * `schemas/calendar_design.py`), so both frontends pass them through without a mapping. The
 * backend bounds every number; this module checks them again before they reach CSS, because
 * a style sheet is the one place where a stray character turns into someone else's rule.
 */
import { contrastRatio } from '../theme-config/contrast.js';
/** Container widths where a tablet and a desktop start (the grid measures its frame). */
export const GRID_BREAKPOINTS = { tablet: '34rem', desktop: '52rem' };
/** The grid without a configuration: what field.css draws on its own. */
export const DEFAULT_GRID = {
    columns: { mobile: 3, tablet: 6, desktop: 6 },
    tile_ratio: { width: 1, height: 1 },
};
/** Only fonts the calendar already loads; another web font would be another request. */
export const NUMERAL_FONT_STACKS = {
    display: 'var(--countdown-font-display)',
    body: 'var(--countdown-font-body)',
    sans: 'arial, helvetica, sans-serif',
    serif: 'georgia, "Times New Roman", serif',
};
export const NUMERAL_FONT_LABELS = {
    display: 'Verlag (Hausschrift)',
    body: 'Crimson Pro',
    sans: 'Arial',
    serif: 'Georgia',
};
const COLOR = /^#[0-9a-f]{6}$/i;
/** An integer in a range, or the fallback. Every number below goes into CSS through this. */
function bounded(value, min, max, fallback) {
    return Number.isInteger(value) && value >= min && value <= max ? value : fallback;
}
function columnsOf(grid) {
    const { mobile, tablet, desktop } = grid.columns;
    return [bounded(mobile, 1, 12, 3), bounded(tablet, 1, 12, 6), bounded(desktop, 1, 12, 6)];
}
/** Doors per row on phone, tablet and desktop, deduplicated: what `arrangeFields` must close. */
export function gridColumnCounts(grid) {
    return [...new Set(columnsOf(grid ?? DEFAULT_GRID))];
}
/** Empty places that close the last row, per screen size. */
export function emptyCounts(grid, cellCount) {
    const [mobile, tablet, desktop] = columnsOf(grid).map((columns) => (columns - (cellCount % columns)) % columns);
    return [mobile, tablet, desktop];
}
/**
 * The style sheet of one configured grid, scoped to its frame.
 *
 * Columns per screen size, the ratio of a door, and which of the empty places at the end are
 * shown. The honeycomb needs its offset rows from the column count, and `:nth-child` takes no
 * custom property, so the rules are written out here rather than in field.css. Every value is
 * an integer from `bounded`; `scope` is stripped to safe characters (`gridScope`).
 */
export function fieldGridCss(options) {
    const frame = `[data-countdown-grid="${options.scope.replace(/[^a-zA-Z0-9_-]/g, '')}"]`;
    const honeycomb = options.layout === 'honeycomb';
    const columns = columnsOf(options.grid);
    const empties = emptyCounts(options.grid, options.cellCount);
    const width = bounded(options.grid.tile_ratio.width, 1, 4, 1);
    const height = bounded(options.grid.tile_ratio.height, 1, 4, 1);
    const rulesFor = (perRow, shown) => {
        const grid = honeycomb
            ? [
                `${frame} .countdown-field-grid--honeycomb { --countdown-grid-columns: ${2 * perRow + 1}; }`,
                // As heavy as the offset rule, so a wider screen undoes the offsets of a narrower one.
                `${frame} .countdown-field-grid--honeycomb > .countdown-field:nth-child(n) { grid-column-start: auto; }`,
                `${frame} .countdown-field-grid--honeycomb > .countdown-field:nth-child(${2 * perRow}n + ${perRow + 1}) { grid-column-start: 2; }`,
            ]
            : [`${frame} .countdown-field-grid { --countdown-grid-columns: ${perRow}; }`];
        const empty = Array.from({ length: Math.max(...empties) }, (_, index) => `${frame} .countdown-field--empty[data-empty-index="${index}"] { display: ${index < shown ? 'block' : 'none'}; }`);
        return [...grid, ...empty].join('\n');
    };
    return [
        honeycomb
            ? ''
            : `${frame} .countdown-field-grid { grid-auto-rows: calc(var(--countdown-grid-cell) * ${height} / ${width}); }`,
        rulesFor(columns[0], empties[0]),
        `@container (min-width: ${GRID_BREAKPOINTS.tablet}) {\n${rulesFor(columns[1], empties[1])}\n}`,
        `@container (min-width: ${GRID_BREAKPOINTS.desktop}) {\n${rulesFor(columns[2], empties[2])}\n}`,
    ]
        .filter(Boolean)
        .join('\n');
}
/**
 * The scope of a grid's style sheet, derived from what the sheet says. Two grids on one page
 * with the same configuration share it, and then also share identical rules; React's `useId`
 * would repeat itself across separately rendered islands and let one grid's rules win over
 * another's.
 */
export function gridScope(layout, grid, cellCount) {
    let hash = 0x811c9dc5;
    for (const character of JSON.stringify([layout, grid, cellCount])) {
        hash ^= character.codePointAt(0) ?? 0;
        hash = Math.imul(hash, 0x01000193);
    }
    return `g${(hash >>> 0).toString(36)}`;
}
/**
 * The custom properties of one door number, for the `style` of a tile. A value that does not
 * pass the check is left out, and field.css falls back to the built-in look for it.
 */
export function doorNumberVariables(style) {
    const variables = {};
    for (const [part, numeral] of [
        ['door', style.door],
        ['open', style.open],
    ]) {
        const prefix = `--countdown-numeral-${part}`;
        variables[`${prefix}-font`] = NUMERAL_FONT_STACKS[numeral.font] ?? NUMERAL_FONT_STACKS.display;
        variables[`${prefix}-weight`] = numeral.weight === 'regular' ? '400' : '700';
        if (COLOR.test(numeral.color)) {
            variables[`${prefix}-color`] = numeral.color;
        }
        variables[`${prefix}-size`] = String(bounded(numeral.size, 5, 80, 38) / 100);
        variables[`${prefix}-x`] = `${bounded(numeral.x, 0, 100, 50)}%`;
        variables[`${prefix}-y`] = `${bounded(numeral.y, 0, 100, 50)}%`;
        variables[`${prefix}-rotate`] = `${bounded(numeral.rotation, -20, 20, 0)}deg`;
    }
    // Behind the logo: the logo is lifted over the door (closed) or the number sunk under the
    // logo (open). Only these two values ever reach the z-index.
    if (style.door.layer === 'behind') {
        variables['--countdown-reveal-over-door'] = '3';
    }
    if (style.open.layer === 'behind') {
        variables['--countdown-numeral-open-z'] = '0';
    }
    return variables;
}
export const DEFAULT_EMBED_LOOK = {
    header: false,
    title: false,
    footer: false,
    decor: true,
    ground: 'scene',
};
/** The four designs the backoffice offers to start from; the switches stay free after. */
export const EMBED_PRESETS = [
    ['Winternacht', 'Nachthimmel mit Mond, Schnee und Wald.', DEFAULT_EMBED_LOOK],
    ['Nacht ruhig', 'Nur der Nachthimmel, ohne Verzierungen.', { ...DEFAULT_EMBED_LOOK, decor: false }],
    [
        'Hell im Hausstil',
        'Helle Fläche, weiße Karte mit Überschrift.',
        { ...DEFAULT_EMBED_LOOK, decor: false, title: true, ground: 'light' },
    ],
    [
        'Ohne Hintergrund',
        'Die Türchen stehen direkt auf der Website.',
        { ...DEFAULT_EMBED_LOOK, decor: false, ground: 'none' },
    ],
];
/** The query parameter that shows a frame in another look than the saved one (D39). */
export const EMBED_LOOK_PARAM = 'darstellung';
const GROUND_WORDS = [
    ['scene', 'nacht'],
    ['light', 'hell'],
    ['none', 'ohne'],
];
const PART_WORDS = [
    ['header', 'kopf'],
    ['title', 'titel'],
    ['footer', 'fuss'],
    ['decor', 'deko'],
];
/**
 * The look a `darstellung` value asks for, `null` without one: `nacht,deko`, `hell,titel`. A part
 * not named is off, a missing ground is the seasonal world, an unknown word is ignored. The
 * backoffice shows an unsaved look this way; it changes nothing but the page it is asked for.
 */
export function parseEmbedLook(value) {
    if (value === null) {
        return null;
    }
    const words = new Set(value.toLowerCase().split(',').map((word) => word.trim()));
    const parts = Object.fromEntries(PART_WORDS.map(([key, word]) => [key, words.has(word)]));
    const ground = GROUND_WORDS.find(([, word]) => words.has(word))?.[0] ?? 'scene';
    return { header: false, title: false, footer: false, decor: false, ...parts, ground };
}
/** The `darstellung` value of a look, the ground first. Letters and commas only. */
export function embedLookParam(look) {
    const ground = GROUND_WORDS.find(([key]) => key === look.ground)?.[1] ?? 'nacht';
    return [ground, ...PART_WORDS.filter(([key]) => look[key]).map(([, word]) => word)].join(',');
}
/**
 * The defaults of the backend's `CalendarStyle`, for the surfaces that draw a calendar
 * without an action behind it (the backoffice samples). The API always sends a full style, so
 * the participant page never falls back on this; `calendar-design.test.ts` holds it against
 * the checked-in OpenAPI document.
 */
export const DEFAULT_CALENDAR_STYLE = {
    door_opening: 'outward',
    door_look: 'perforated',
    today_highlight: 'lights',
    past_marker: 'stripe',
    scene_color: null,
    filler_count: 0,
    filler_ornaments: true,
    decorations: false,
    number_wobble: 4,
    organizer_shown: true,
    header_name_shown: true,
    embed: DEFAULT_EMBED_LOOK,
};
export function logoPlacementVariables(placement) {
    return {
        '--countdown-logo-x': `${bounded(placement.x, 0, 100, 50)}%`,
        '--countdown-logo-y': `${bounded(placement.y, 0, 100, 50)}%`,
        '--countdown-logo-size': `${bounded(placement.size, 20, 100, 70)}%`,
    };
}
/** The footer reads at AA on every shade of the night, or the colour does not get through. */
export const SCENE_MIN_CONTRAST = 4.5;
/** The footer on the night is the inverse ink at this share (`seasons/christmas.css`). */
export const FOOTER_TEXT_SHARE = 0.85;
const toRgb = (color) => [1, 3, 5].map((at) => Number.parseInt(color.slice(at, at + 2), 16));
const toHex = (color) => `#${color.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
const mix = (a, b, share) => a.map((channel, index) => Math.round(channel * share + (b[index] ?? 0) * (1 - share)));
/** The brand mix of `seasons/christmas.css`: each shade is this share of a colour over its base. */
const NIGHT_MIX = { share: 0.26, base: [10, 18, 34] };
const DEEP_MIX = { share: 0.14, base: [4, 7, 15] };
const HORIZON_MIX = { share: 0.28, base: [22, 32, 52] };
/**
 * The shade `shade` that the brand mix draws next to the middle `night`: the colour that mixes
 * to this night is solved back, then mixed the way the shade is. Linear, so any typed night
 * gets exactly the gradient the old mixed colours had.
 */
const shadeOfNight = (night, shade) => night.map((value, index) => {
    const source = (value - (1 - NIGHT_MIX.share) * (NIGHT_MIX.base[index] ?? 0)) / NIGHT_MIX.share;
    const mixed = source * shade.share + (shade.base[index] ?? 0) * (1 - shade.share);
    return Math.min(255, Math.max(0, Math.round(mixed)));
});
/**
 * The three shades of a night of this colour (D31, 12.10.2026): the colour itself in the
 * middle, the top and the horizon as the brand mix would draw them around that middle.
 */
function sceneShades(color) {
    const rgb = toRgb(color);
    return {
        deep: toHex(shadeOfNight(rgb, DEEP_MIX)),
        night: color.toLowerCase(),
        horizon: toHex(shadeOfNight(rgb, HORIZON_MIX)),
    };
}
/** The weakest contrast of the footer text over the night of this colour, `null` for no colour. */
export function sceneFooterContrast(color) {
    if (!COLOR.test(color)) {
        return null;
    }
    return Math.min(...Object.values(sceneShades(color)).map((shade) => contrastRatio(toHex(mix([255, 255, 255], toRgb(shade), FOOTER_TEXT_SHARE)), shade) ?? 0));
}
/**
 * A colour for the night, or `null` for the brand colour: only a `#rrggbb` on which the footer
 * stays readable gets through. A lighter one falls back to the night of the brand colour.
 */
export function sceneColor(style) {
    const color = style?.scene_color ?? null;
    return color !== null && (sceneFooterContrast(color) ?? 0) >= SCENE_MIN_CONTRAST ? color : null;
}
/**
 * What a typed colour code means: a lower-case `#rrggbb` (the `#` may be left out), `null` for
 * an empty field, which is the brand colour, and `undefined` while it is no colour yet. It
 * does not judge the contrast; `sceneFooterContrast` does.
 */
export function parseSceneInput(text) {
    const value = text.trim();
    if (value === '') {
        return null;
    }
    const color = value.startsWith('#') ? value : `#${value}`;
    return COLOR.test(color) ? color.toLowerCase() : undefined;
}
/**
 * The night of an action as the body's custom properties, which `seasons/christmas.css` reads;
 * `undefined` without a colour of its own (or with one too light), so the brand night stays.
 */
export function sceneStyle(style) {
    const color = sceneColor(style);
    if (color === null) {
        return undefined;
    }
    const { deep, night, horizon } = sceneShades(color);
    return `--countdown-scene-deep: ${deep}; --countdown-scene-color: ${night}; --countdown-scene-horizon: ${horizon}`;
}
/**
 * Quick picks for the night in the backoffice. The first is the brand colour itself; the others
 * are the nights the old presets showed while the colour was mixed (26 % over rgb(10 18 34)).
 */
export const SCENE_PRESETS = [
    [null, 'Markenfarbe'],
    ['#2c1525', 'Weinrot'],
    ['#0f213e', 'Nachtblau'],
    ['#0f2528', 'Tannengrün'],
    ['#1b1839', 'Violett'],
];
/**
 * The night of `seasons/christmas.css` as one background, for the backoffice preview, which
 * does not load the seasonal world. A `#rrggbb` is the action's own colour and draws its three
 * shades, through the same gate as `sceneColor`: one too light shows the brand night, as on
 * the participant page. Anything else (the brand colour as a `var()`) is mixed with the same
 * shares as the stylesheet. `calendar-design.test.ts` compares both.
 */
export function sceneBackground(color) {
    if (COLOR.test(color)) {
        const own = sceneColor({ scene_color: color });
        if (own === null) {
            return sceneBackground('var(--countdown-color-brand)');
        }
        const { deep, night, horizon } = sceneShades(own);
        return `linear-gradient(180deg, ${deep}, ${night} 45%, ${horizon})`;
    }
    const deep = `color-mix(in srgb, ${color} 14%, rgb(4 7 15))`;
    const night = `color-mix(in srgb, ${color} 26%, rgb(10 18 34))`;
    const horizon = `color-mix(in srgb, ${color} 28%, rgb(22 32 52))`;
    return `linear-gradient(180deg, ${deep}, ${night} 45%, ${horizon})`;
}
/** The tilt of one door's number, from -max to max degrees, fixed by its position. */
export function numberWobble(position, max) {
    const limit = bounded(max, 0, 10, 0);
    if (limit === 0 || !Number.isInteger(position)) {
        return 0;
    }
    // An integer hash rather than a step, so the tilts do not run up in a ramp; the same door
    // tilts the same way on every visit.
    let hash = Math.imul(position, 0x9e3779b1) >>> 0;
    hash ^= hash >>> 15;
    hash = Math.imul(hash, 0x85ebca6b) >>> 0;
    hash ^= hash >>> 13;
    return ((hash >>> 0) % (2 * limit + 1)) - limit;
}
/** The six own shapes of `ornaments.css`. */
export const ORNAMENTS = ['star', 'snowflake', 'tree', 'bauble', 'gift', 'candle'];
/** The shape of the n-th empty place: neighbours differ, the order is fixed. */
export function ornamentFor(index) {
    const at = (((index * 5 + 1) % ORNAMENTS.length) + ORNAMENTS.length) % ORNAMENTS.length;
    return ORNAMENTS[at] ?? 'star';
}
/**
 * The shape on a door's corner, for about one door in five, or `null`. Fixed by the position,
 * so a door keeps its star from visit to visit and the page stays one cacheable answer.
 */
export function decorFor(position) {
    if (!Number.isInteger(position) || (position * 7 + 3) % 5 !== 0) {
        return null;
    }
    const corners = ['top-right', 'bottom-left', 'top-left', 'bottom-right'];
    return {
        ornament: ornamentFor(position * 3),
        corner: corners[position % corners.length] ?? 'top-right',
    };
}
//# sourceMappingURL=calendar-design.js.map